import {makeApiCall} from '../core';
import type {ItemEnvelope, NotificationType} from '@ruta/shared';
import {serializeExcludeTypesParam} from '@ruta/shared';

// Legacy types still used in webapp (to be migrated)
import type {AdminNotificationRecord} from '../types';

// =====================
// Notifications API
// =====================

export interface GetNotificationsResponse {
  notifications: AdminNotificationRecord[];
  unreadCount: number;
  total: number;
  webappBaseUrl?: string;
  nextKey?: string;
}

export const getNotifications = async (
  limit: number = 25,
  showRead: boolean = false,
  lastKey?: string,
  excludeTypes?: NotificationType[],
): Promise<GetNotificationsResponse> => {
  const params = new URLSearchParams({
    limit: limit.toString(),
    showRead: showRead.toString(),
  });
  if (lastKey) params.set('lastKey', lastKey);
  if (excludeTypes && excludeTypes.length > 0) {
    params.set('excludeTypes', serializeExcludeTypesParam(excludeTypes));
  }

  const response = await makeApiCall<ItemEnvelope<GetNotificationsResponse>>(
    'GET',
    `/notifications?${params}`,
  );
  return response.data;
};

export const updateNotification = async (
  notificationId: string,
  updates: {
    read?: boolean;
    priority?: 'low' | 'medium' | 'high' | 'urgent';
    archived?: boolean;
  },
): Promise<void> => {
  return makeApiCall('PATCH', `/notifications/${notificationId}`, updates);
};

// Convenience function for marking as read
export const markNotificationAsRead = async (notificationId: string): Promise<void> => {
  return updateNotification(notificationId, { read: true });
};

// Convenience function for marking as unread
export const markNotificationAsUnread = async (notificationId: string): Promise<void> => {
  return updateNotification(notificationId, { read: false });
};

// Mark all notifications as read
export const markAllNotificationsAsRead = async (): Promise<{ updatedCount: number }> => {
  const response = await makeApiCall<ItemEnvelope<{ updatedCount: number }>>(
    'PATCH',
    '/notifications',
    { read: true },
  );
  return response.data;
};

// =====================
// Push Notification Devices (RESTful)
// =====================

export const registerPushDevice = async (
  deviceData: {
    token: string;
    platform: 'ios' | 'android' | 'web';
    userId?: string;
  },
): Promise<{ tokenId: string }> => {
  const response = await makeApiCall<ItemEnvelope<{ tokenId: string }>>(
    'POST',
    '/notifications/devices',
    deviceData,
  );
  return response.data;
};
